import { useState, useRef, useEffect } from "react";

/**
 * Multi-select filter that lives in a column header. No selection = no filter (all rows), which keeps
 * "I haven't touched it" and "I want everything" the same state. Counts come from the whole table, not
 * the current page, so an option never looks empty just because it's off-screen.
 */
export function ColumnFilter<T extends string>({
  title,
  options,
  selected,
  onChange,
}: {
  title: string;
  options: { value: T; label: string; count?: number }[];
  selected: T[];
  onChange: (next: T[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = (v: T) => onChange(selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v]);
  const active = selected.length > 0;
  // A dropdown of 490 brands is a wall, not a filter. Past a dozen options, let people type.
  const searchable = options.length > 12;
  const shown = q.trim()
    ? options.filter((o) => `${o.label} ${o.value}`.toLowerCase().includes(q.trim().toLowerCase()))
    : options;

  return (
    <div ref={box} className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1 text-xs uppercase tracking-wide ${active ? "font-semibold text-indigo-700" : "text-slate-500 hover:text-slate-700"}`}
        title={active ? `Filtered: ${selected.join(", ")}` : `Filter by ${title.toLowerCase()}`}
      >
        {title}
        <span className={active ? "text-indigo-600" : "text-slate-300"}>▾</span>
        {active && <span className="rounded-full bg-indigo-100 px-1.5 text-[10px] font-semibold text-indigo-700">{selected.length}</span>}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 min-w-56 rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
          {searchable && (
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`search ${options.length} options…`}
              className="mb-1 w-full rounded border border-slate-200 px-2 py-1 text-xs normal-case tracking-normal outline-none focus:border-indigo-400"
            />
          )}
          <div className="max-h-64 overflow-auto">
          {shown.map((o) => (
            <label key={o.value} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm normal-case tracking-normal text-slate-700 hover:bg-indigo-50">
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} className="accent-indigo-600" />
              <span className="flex-1">{o.label}</span>
              {o.count != null && <span className="text-[11px] tabular-nums text-slate-400">{o.count.toLocaleString()}</span>}
            </label>
          ))}
          {!shown.length && <div className="px-2 py-1.5 text-xs text-slate-400">{options.length ? "no match" : "nothing to filter yet"}</div>}
          </div>
          <div className="mt-1 flex justify-between border-t border-slate-100 px-2 pt-1">
            <button onClick={() => { onChange([]); setQ(""); }} className="text-[11px] text-slate-500 hover:text-slate-800 disabled:opacity-40" disabled={!active}>clear</button>
            <button onClick={() => setOpen(false)} className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800">done</button>
          </div>
        </div>
      )}
    </div>
  );
}
